import { motion, useAnimation } from 'framer-motion'
import { useState, useRef, useEffect, useCallback } from 'react'
import { debounce } from '../data-display/data-table/useDataTable.tsx'
import { MainContainer } from '../layout/MainContainer.tsx'

interface MenuItem {
  label: string
  sectionId: string
}

interface TabsPageSectionProps {
  menuItems: MenuItem[]
}

export const TabsPageSection: React.FC<TabsPageSectionProps> = ({
  menuItems,
}) => {
  const [activeItem, setActiveItem] = useState(menuItems?.[0]?.label)
  const navRef = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef(0)
  const controls = useAnimation()
  const [isSticky, setIsSticky] = useState(false)
  const tabsContainerRef = useRef<HTMLDivElement>(null)
  const [shadow, setShadow] = useState(false)

  const isClickScrollingRef = useRef(false)

  const scrollActiveTabIntoView = useCallback(() => {
    if (tabsContainerRef.current) {
      const activeTabElement: any = tabsContainerRef.current.querySelector(
        `[data-tab="${activeItem}"]`,
      )
      if (activeTabElement) {
        const containerRect = tabsContainerRef.current.getBoundingClientRect()
        const tabRect = activeTabElement.getBoundingClientRect()

        if (
          tabRect.left < containerRect.left ||
          tabRect.right > containerRect.right
        ) {
          tabsContainerRef.current.scrollTo({
            left:
              activeTabElement.offsetLeft -
              containerRect.width / 2 +
              tabRect.width / 2,
            behavior: 'smooth',
          })
        }
      }
    }
  }, [activeItem])

  useEffect(() => {
    scrollActiveTabIntoView()
  }, [activeItem, scrollActiveTabIntoView])

  const updateActiveItem = useCallback(() => {
    if (isClickScrollingRef.current) return

    const debouncedUpdate = debounce(() => {
      const currentScrollY = window.scrollY
      for (let i = menuItems.length - 1; i >= 0; i--) {
        const menuItem = menuItems[i]
        if (!menuItem) continue
        const section = document.getElementById(menuItem.sectionId)
        if (section && section.offsetTop <= currentScrollY + 100) {
          setActiveItem(menuItem.label)
          break
        }
      }
    }, 100)

    debouncedUpdate()
    // setShadow(false)
  }, [menuItems])

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY
    const isMobile = window.innerWidth < 1024
    const headerHeight = 0
    const headerHideThreshold = isMobile ? 250 : 500

    setIsSticky(currentScrollY >= headerHideThreshold)
    setShadow(true)

    if (currentScrollY < lastScrollY.current) {
      if (currentScrollY < headerHideThreshold) {
        void controls.start({ y: 0, transition: { duration: 0 } })
      } else {
        void controls.start({ y: headerHeight, transition: { duration: 0.15 } })
      }
    } else {
      if (currentScrollY < headerHideThreshold) {
        void controls.start({ y: 0, transition: { duration: 0.15 } })
      } else {
        void controls.start({ y: 0, transition: { duration: 0.15 } })
      }
    }

    lastScrollY.current = currentScrollY
    updateActiveItem()
  }, [controls, updateActiveItem])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const handleTabClick = useCallback((item: MenuItem) => {
    isClickScrollingRef.current = true
    setActiveItem(item.label)
    const sectionId = item.sectionId.replace(/^#/, '')
    const section = document.getElementById(sectionId)
    if (section) {
      const yOffset = -60 // Adjust this value based on your header height
      const y =
        section.getBoundingClientRect().top + window.pageYOffset + yOffset
      window.scrollTo({ top: y, behavior: 'smooth' })

      const checkScrollCompletion = () => {
        const currentY = window.pageYOffset
        if (Math.abs(currentY - y) < 2) {
          isClickScrollingRef.current = false
        } else {
          requestAnimationFrame(checkScrollCompletion)
        }
      }
      requestAnimationFrame(checkScrollCompletion)
    } else {
      isClickScrollingRef.current = false
    }
  }, [])

  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout>
    const handleScrollChange = () => {
      scrollTimeout = setTimeout(() => {
        setShadow(false)
      }, 1200)
    }

    window.addEventListener('scroll', handleScrollChange)

    return () => {
      window.removeEventListener('scroll', handleScrollChange)
      clearTimeout(scrollTimeout)
      // setShadow(false); // Clean up: set shadow false on unmount
    }
  }, [lastScrollY.current])

  return (
    <motion.nav
      className={`
        top-[60px] z-20 h-14 border-b border-neutral-75 bg-white !pb-0
        text-base-bold

        lg:top-[86px] lg:h-[59px] lg:rounded-t-base

        ${isSticky ? `sticky` : ''}
        ${shadow ? 'shadow-md' : ''}
      `}
      animate={controls}
      ref={navRef}
    >
      <MainContainer className="h-full !px-0 !py-0">
        <div
          className="relative h-full overflow-x-auto overflow-y-hidden"
          ref={tabsContainerRef}
        >
          <ul className={`flex h-full items-center`}>
            {menuItems.map((item, i) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
              <li
                key={item.label}
                className={`
                  relative inline-block h-full flex-grow border-r
                  border-neutral-75 px-6 text-center

                  lg:w-[222px]

                  ${i === menuItems.length - 1 && `border-r-0`}
                `}
                onClick={(e) => {
                  e.preventDefault()
                  handleTabClick(item)
                }}
              >
                <button
                  className={`
                    h-full px-3 py-0 text-base-bold text-neutral-900
                    transition-colors duration-300 ease-in-out

                    lg:px-[18px] lg:py-4

                    ${
                      activeItem === item.label
                        ? 'text-primary-600'
                        : `lg:hover:text-primary-600`
                    }
                  `}
                  data-tab={item.label}
                >
                  {item.label}
                </button>
                {activeItem === item.label && (
                  <motion.span
                    className={`
                      absolute bottom-0 left-0 h-[2px] w-full bg-primary-600

                      lg:h-[3px]
                    `}
                    layoutId="activeTab"
                    initial={false}
                    transition={
                      item.label !== menuItems[menuItems.length - 1]?.label
                        ? {
                            type: 'spring',
                            stiffness: 500,
                            damping: 30,
                          }
                        : {
                            type: 'tween',
                            duration: 0.3,
                          }
                    }
                    style={{ width: '100%' }}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      </MainContainer>
    </motion.nav>
  )
}
